import { NaturezaAnalise } from "@/@types/NaturezaAnalise";
import { IAnaliseLaboratorial } from "@/interfaces/IAnaliseLaboratorial";
import { IHistoricoAnaliseLaboratorial } from "@/interfaces/IHistoricoAnaliseLaboratorial";
import { IListaAnaliseLaboratorial } from "@/interfaces/IListaAnaliseLaboratorial";
import { api } from "@/services/api";
import { requisicaoAnaliseLaboratorialAction } from "@/store/actions/requisicaoAnaliseLaboratorial";
import { getRequisicoesAnaliseLaboratorial } from "@/store/reducers/requisicaoAnaliseLaboratorial";
import { useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";

export const useAnaliseLaboratorial = () => {
  const { user } = useAuth();
  const appDispatch = useDispatch();
  const { add, remove, update } = requisicaoAnaliseLaboratorialAction;
  const listaRequisicoes = useSelector(getRequisicoesAnaliseLaboratorial);

  const addRequisicao = (item: IAnaliseLaboratorial) => {
    appDispatch(add(item));
  };

  const removeRequisicao = (offlineId: number) => {
    appDispatch(remove(offlineId));
  };

  const updateRequisicao = (item: IAnaliseLaboratorial) => {
    appDispatch(update(item));
  };

  const submitRequisicaoApi = async (
    item: Omit<IAnaliseLaboratorial, "id" | "situacao">
  ) => {
    const { data } = await api.post<IAnaliseLaboratorial>(
      "/requisicoes-analises-laboratoriais",
      item,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      }
    );
    return data;
  };

  const cancelarAnaliseLaboratorialApi = async (id: number, motivo: string) => {
    await api.patch(
      `/requisicoes-analises-laboratoriais/${id}/cancelar`,
      { motivo },
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
  };

  const getAnaliseLaboratorialByIdApi = async (id: number) => {
    const { data } = await api.get<IAnaliseLaboratorial>(
      `/requisicoes-analises-laboratoriais/${id}`,
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
    return data;
  };

  const getAnalisesLaboratoriaisApi = async ({
    estabelecimentoId,
    dataColeta,
    codigo,
    produtoOrigemAnimalId,
    page = 1,
  }: {
    estabelecimentoId: number;
    dataColeta?: string;
    codigo?: string;
    produtoOrigemAnimalId?: number;
    page?: number;
  }) => {
    const { data } = await api.get<IListaAnaliseLaboratorial>(
      "/requisicoes-analises-laboratoriais",
      {
        params: {
          estabelecimentoId,
          dataColeta,
          codigo,
          produtoOrigemAnimalId,
          size: 20,
          page: page - 1,
        },
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
    return data;
  };

  const getRequisicaoOfflineById = (offlineId: number) => {
    return listaRequisicoes.find((item) => item.offlineId === offlineId);
  };

  const submitCertificadoApi = async (
    requisicaoId: number,
    formData: FormData
  ) => {
    const { data } = await api.patch(
      `/requisicoes-analises-laboratoriais/${requisicaoId}/certificado`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  };

  const getAnexoApi = async (nomeUpload: string) => {
    try {
      const { data } = await api.get<Blob>(
        `/requisicoes-analises-laboratoriais/anexo/${nomeUpload}`,
        {
          headers: { Authorization: `Bearer ${user?.access_token}` },
          responseType: "blob",
        }
      );
      return data;
    } catch (error) {
      const axiosError = error as any;
      if (error instanceof Error && "response" in error) {
        if (axiosError.response?.data instanceof Blob) {
          const text = await axiosError.response.data.text();
          try {
            const jsonError = JSON.parse(text);
            axiosError.response.data = jsonError;
          } catch {
            axiosError.response.data = { detail: text };
          }
        }
      }

      throw error;
    }
  };

  const getHistoricoAnalisesApi = async ({
    estabelecimentoId,
    naturezaAnaliseOficial,
    produtoAutorizadoId,
  }: {
    estabelecimentoId: number;
    naturezaAnaliseOficial: NaturezaAnalise;
    produtoAutorizadoId?: number;
  }) => {
    const { data } = await api.get<IHistoricoAnaliseLaboratorial[]>(
      `/requisicoes-analises-laboratoriais/historico`,
      {
        params: {
          estabelecimentoId,
          naturezaAnaliseOficial,
          produtoAutorizadoId,
        },
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
    return data;
  };

  return {
    addRequisicao,
    removeRequisicao,
    updateRequisicao,
    listaRequisicoes,
    submitRequisicaoApi,
    cancelarAnaliseLaboratorialApi,
    getAnaliseLaboratorialByIdApi,
    getAnalisesLaboratoriaisApi,
    getRequisicaoOfflineById,
    submitCertificadoApi,
    getAnexoApi,
    getHistoricoAnalisesApi,
  };
};
